"use client";

import { useState } from "react";
import { Box, Typography, Button, Alert } from "@mui/material";
import { theme } from "@/lib/theme";

type SimulationStep = "initial" | "bait-and-switch" | "educational";

export const MoneyTransferCard = () => {
  const [step, setStep] = useState<SimulationStep>("initial");
  const [sendAmount] = useState(100);

  const initialExchangeRate = 124.0;
  const initialFee = 100.99;
  const baitSwitchExchangeRate = 22.0;
  const baitSwitchFee = 135.99;

  const exchangeRate = step === "initial" ? initialExchangeRate : baitSwitchExchangeRate;
  const fee = step === "initial" ? initialFee : baitSwitchFee;
  const recipientAmount = sendAmount * exchangeRate;
  const totalCost = sendAmount + fee;

  const handleSendMoney = () => {
    setStep("bait-and-switch");
  };

  const handleProceed = () => {
    setStep("educational");
  };

  const handleLearnMore = () => {
    setStep("initial");
    const nextSection = document.getElementById("chapter-00");
    if (nextSection) {
      const heroHeight = window.innerHeight;
      window.scrollTo({ top: heroHeight, behavior: "smooth" });
    }
  };

  if (step === "educational") {
    return (
      <Box
        id="educational-panel"
        sx={{
          backgroundColor: theme.colors.white,
          borderRadius: "20px",
          padding: { xs: 2.5, sm: 3.5 },
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
          textAlign: "left",
          minHeight: "400px",
        }}
      >
        <Typography
          sx={{
            fontFamily: theme.fonts.sans,
            fontSize: { xs: "32px", sm: "48px" },
            fontWeight: theme.fonts.weights.bold,
            color: theme.colors.black,
            lineHeight: 1.1,
            mb: 2,
          }}
        >
          You&apos;ve just experienced a harmful design pattern.
        </Typography>
        <Typography
          sx={{
            fontFamily: theme.fonts.sans,
            fontSize: { xs: "18px", sm: "22px" },
            color: theme.colors.gray[600],
            mb: 3,
            lineHeight: 1.4,
          }}
        >
          Hidden costs and fees like this erode user trust.
        </Typography>
        <Button
          fullWidth
          onClick={handleLearnMore}
          sx={{
            backgroundColor: theme.colors.purple,
            color: theme.colors.white,
            padding: "12px",
            borderRadius: "10px",
            fontSize: { xs: "16px", sm: "18px" },
            fontWeight: theme.fonts.weights.bold,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#5a53d5",
            },
          }}
        >
          Learn More
        </Button>
      </Box>
    );
  }

  return (
    <Box
      id="transfer-form"
      sx={{
        backgroundColor: theme.colors.white,
        borderRadius: "20px",
        padding: { xs: 2.5, sm: 3.5 },
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Typography
        sx={{
          fontFamily: theme.fonts.sans,
          fontSize: { xs: "18px", sm: "20px" },
          fontWeight: theme.fonts.weights.bold,
          mb: "6px !important",
          color: theme.colors.black,
        }}
      >
        You send
      </Typography>
      <Box
        sx={{
          fontSize: { xs: "28px", sm: "32px" },
          fontWeight: theme.fonts.weights.bold,
          padding: "10px 14px",
          borderRadius: "10px",
          backgroundColor: theme.colors.gray[50],
          border: `1px solid ${theme.colors.gray[200]}`,
          mb: 1,
          color: theme.colors.black,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>{sendAmount}</span>
        <Typography
          sx={{
            fontSize: { xs: "28px", sm: "32px" },
            fontWeight: theme.fonts.weights.bold,
            color: theme.colors.gray[400],
            lineHeight: 1,
            m: "0px !important",
          }}
        >
          USD
        </Typography>
      </Box>

      <Typography
        sx={{
          fontFamily: theme.fonts.sans,
          fontSize: { xs: "18px", sm: "20px" },
          fontWeight: theme.fonts.weights.bold,
          mb: "6px !important",
          color: theme.colors.black,
        }}
      >
        Recipient gets
      </Typography>
      <Box
        sx={{
          fontSize: { xs: "28px", sm: "32px" },
          fontWeight: theme.fonts.weights.bold,
          padding: "10px 14px",
          borderRadius: "10px",
          backgroundColor: theme.colors.gray[50],
          border: `1px solid ${theme.colors.gray[200]}`,
          mb: 0.75,
          color: theme.colors.black,
        }}
      >
        {recipientAmount.toFixed(0)} GLM
      </Box>

      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              color: theme.colors.gray[500],
              my: "3px !important",
            }}
          >
            Special rate
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "13px", sm: "14px" },
              color: theme.colors.gray[600],
              fontWeight: theme.fonts.weights.medium,
              my: "3px !important",
            }}
          >
            1 USD = {exchangeRate.toFixed(2)} GLM
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            minHeight: "20px",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              my: "3px !important",
              fontSize: { xs: "13px", sm: "14px" },
              color: theme.colors.gray[500],
            }}
          >
            Fee
          </Typography>
          <Typography
            sx={{
              my: "3px !important",
              fontSize: { xs: "13px", sm: "14px" },
              color: theme.colors.gray[600],
              fontWeight: theme.fonts.weights.medium,
            }}
          >
            {fee.toFixed(2)} USD
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            borderTop: `1px solid ${theme.colors.gray[200]}`,
            pt: 0.5,
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: theme.fonts.weights.bold,
              color: theme.colors.black,
            }}
          >
            Total Cost
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "16px", sm: "18px" },
              fontWeight: theme.fonts.weights.bold,
              color: theme.colors.purple,
            }}
          >
            {totalCost.toFixed(2)} USD
          </Typography>
        </Box>
      </Box>

      {step === "bait-and-switch" && (
        <Alert
          severity="error"
          sx={{
            mb: 1.5,
            backgroundColor: "#FFD4D4",
            color: theme.colors.black,
            "& .MuiAlert-icon": {
              color: "#D32F2F",
            },
            fontSize: { xs: "11px", sm: "12px" },
            borderRadius: "10px",
          }}
        >
          A processing fee of 35.00 USD has been applied for cross-border liquidity balancing (Reg. 12-B). Previous exchange rate is now 1 USD = 22 GLM to reflect current partner-network stability.
        </Alert>
      )}

      <Button
        fullWidth
        onClick={step === "initial" ? handleSendMoney : handleProceed}
        sx={{
          backgroundColor: theme.colors.purple,
          color: theme.colors.white,
          padding: "12px",
          borderRadius: "10px",
          fontSize: { xs: "16px", sm: "18px" },
          fontWeight: theme.fonts.weights.bold,
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#5a53d5",
          },
        }}
      >
        {step === "initial" ? "Send Money" : "Proceed"}
      </Button>
    </Box>
  );
};
